import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AgentThreadsTable } from "@/Agents/Threads/AgentThreadsTable";
import { useAuth } from "@/providers";
import { FormPageHeading, PageBreadcrumbs } from "@/shared/components";
import { PageLayout } from "@/shared/components/PageLayout";
import type { Filter } from "@/shared/components/ResourceView";
import { useAgent } from "@/shared/domain/agents/hooks";
import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./AgentThreadsPage.module.css";

/**
 * "Threads" page of the agent detail UI (``/agents/:agentId/threads``).
 * Renders the shared :class:`AgentThreadsTable` pinned to the current
 * agent via a ``created_from_agent_id`` scope filter, so the table shows
 * every thread launched from this agent template.
 *
 * The scope is enforced server-side and cannot be widened by the user's
 * filter widget: ``composeFilters`` wraps the scope and the user filters
 * in an outer AND, so the agent pin survives even when the user picks
 * ``OR`` match mode for their own filters. The redundant Agent column
 * (every row would repeat this agent) starts hidden; the column picker
 * can re-show it.
 *
 * The agent record itself is needed only for the breadcrumb / page
 * title, but the table is still gated on it resolving — a failed load
 * means the agent id is bad or unreadable, and a scoped-empty table
 * would misreport that as "no threads".
 */
export function AgentThreadsPage() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  if (agentId === undefined) {
    // Programming error — the route declaration registers
    // `/agents/:agentId/threads` so React Router guarantees the param
    // is set by the time this component mounts.
    throw new Error("agentId not provided");
  }
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentQuery = useAgent(agentId, { orgId });

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "created_from_agent_id",
        comparator: "EQUALS",
        values: [agentId],
      },
    ],
    [agentId],
  );

  const agentName = agentQuery.data?.name ?? "Agent";
  const pageTitle = `Roboto - ${agentName} - Threads`;
  const headerContentLeft = (
    <PageBreadcrumbs>
      <Link to={Goto.route.Agents}>Agents</Link>
      <Link to={Goto.createAgentRoute(agentId)}>{agentName}</Link>
      <span>Threads</span>
    </PageBreadcrumbs>
  );

  // Loading and error branches keep the breadcrumb mounted, so a failed or
  // slow load can still be backed out of to the agent or the list.
  let body: React.ReactNode;
  if (agentQuery.isError) {
    body = (
      <div className={styles.error}>
        Failed to load agent. {agentQuery.error?.message ?? ""}
      </div>
    );
  } else if (agentQuery.isLoading || agentQuery.data === undefined) {
    // The ``data`` check covers the window where ``orgId`` is still
    // resolving: ``useAgent`` is disabled then, and a disabled query
    // reports ``isLoading: false`` with no data — falling through would
    // mount the scoped-empty table, misreading as "no threads". Same
    // guard as ``LaunchAgent``.
    body = <div>Loading agent&hellip;</div>;
  } else {
    body = (
      <div className={styles.body}>
        <AgentThreadsTable
          scope={scope}
          defaultHiddenColumnIds={["created_from_agent_id"]}
          noData={{ title: "No threads from this agent yet" }}
          trailingActions={
            <Button
              variant="outlined"
              onClick={() => {
                void navigate(Goto.createAgentLaunchRoute(agentId));
              }}
            >
              Launch
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
      <div className={styles.page}>
        <FormPageHeading
          title="Threads"
          subtitle="Every thread launched from this agent."
        />
        {body}
      </div>
    </PageLayout>
  );
}
